import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { seoArticles } from '@/mocks/articles';

export default function ArticlePreview() {
  const { t } = useTranslation();
  const sectionRef = useScrollAnimation();

  return (
    <section ref={sectionRef} className="py-16 md:py-24 bg-background-100">
      <div className="w-full max-w-7xl mx-auto px-6 md:px-10">
        <div className="text-center mb-12 md:mb-16 animate-on-scroll">
          <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-semibold tracking-wider uppercase mb-4">
            {t('article_preview_tag')}
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 font-heading">
            Poradniki i artykuły SEO
          </h2>
          <p className="text-sm md:text-base text-foreground-600 mt-4 max-w-xl mx-auto">
            Wszystko co musisz wiedzieć o Kakobuy, zakupach z Chin i najlepszych znaleziskach. Kompletna baza wiedzy.
          </p>
        </div>

        {/* Featured Pillar Article */}
        <div className="mb-10 animate-on-scroll">
          <Link
            to="/kakobuy-spreadsheet"
            className="group relative overflow-hidden rounded-2xl bg-background-50 border border-background-200/60 cursor-pointer transition-all duration-300 hover:border-accent-300 hover:shadow-md flex flex-col md:flex-row"
          >
            <div className="md:w-2/5 aspect-[16/10] md:aspect-auto overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20bright%20workspace%20with%20laptop%20displaying%20colorful%20product%20catalog%20spreadsheet%20surrounded%20by%20fashion%20items%20and%20shopping%20bags%20on%20light%20desk%20natural%20daylight%20from%20large%20window%20creating%20fresh%20energetic%20atmosphere%20lifestyle%20entrepreneur%20aesthetic%20editorial%20photography&width=800&height=600&seq=pillar-article-card-unique&orientation=landscape"
                alt="Kakobuy Spreadsheet poradnik"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
            </div>
            <div className="md:w-3/5 p-6 md:p-8 flex flex-col justify-center">
              <span className="inline-block px-3 py-1 rounded-full bg-accent-500 text-background-50 text-xs font-semibold tracking-wider mb-3 w-fit">
                Polecany poradnik
              </span>
              <h3 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading mb-3 group-hover:text-accent-700 transition-colors duration-300">
                Kakobuy Spreadsheet – kompletny przewodnik 2026
              </h3>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                Dowiedz się czym jest Kakobuy Spreadsheet, jak z niego korzystać i dlaczego warto. Kompletny poradnik dla kupujących z Chin – 2000+ słów eksperckiej wiedzy.
              </p>
              <span className="inline-flex items-center gap-2 text-sm font-semibold text-accent-600 group-hover:text-accent-700 transition-colors whitespace-nowrap">
                {t('btn_read_article')}
                <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1"></i>
              </span>
            </div>
          </Link>
        </div>

        {/* Shopping Guide Banner */}
        <div className="mb-10 animate-on-scroll">
          <Link
            to="/poradnik-zakupowy"
            className="group relative overflow-hidden rounded-2xl bg-gradient-to-r from-secondary-100 via-background-50 to-accent-100 border border-background-200/60 cursor-pointer transition-all duration-300 hover:shadow-md block p-6 md:p-8"
          >
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-accent-500 flex items-center justify-center flex-shrink-0 shadow-lg shadow-accent-900/20">
                <i className="ri-compass-3-line text-background-50 text-2xl md:text-3xl"></i>
              </div>
              <div className="flex-1 text-center md:text-left">
                <span className="inline-block px-3 py-1 rounded-full bg-secondary-200 text-secondary-800 text-xs font-semibold tracking-wider uppercase mb-3">
                  Nowość
                </span>
                <h3 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading mb-2 group-hover:text-accent-700 transition-colors">
                  Poradnik zakupowy Kakobuy – Jak kupować mądrze?
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed max-w-2xl">
                  Plan zakupowy w 5 krokach, przegląd wszystkich kategorii i 6 złotych zasad bezpiecznych zakupów. Wszystko w jednym miejscu.
                </p>
              </div>
              <div className="flex-shrink-0">
                <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-all duration-300 whitespace-nowrap">
                  <i className="ri-book-open-line"></i>
                  Czytaj poradnik
                </span>
              </div>
            </div>
          </Link>
        </div>

        {/* 9 SEO Article Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {seoArticles.map((article, idx) => (
            <Link
              key={article.slug}
              to={`/artykul/${article.slug}`}
              className="animate-on-scroll group bg-background-50 rounded-2xl border border-background-200/60 overflow-hidden cursor-pointer transition-all duration-300 hover:border-accent-300 hover:shadow-md"
              style={{ transitionDelay: `${idx * 60}ms` }}
            >
              <div className="aspect-[16/10] overflow-hidden">
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="p-5">
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <span className="inline-block px-2.5 py-0.5 rounded-full bg-accent-100 text-accent-800 text-xs font-semibold tracking-wider uppercase whitespace-nowrap">
                    {article.category}
                  </span>
                  <span className="text-xs text-foreground-400 whitespace-nowrap">{article.readTime} min czytania</span>
                </div>
                <h3 className="text-sm md:text-base font-bold text-foreground-900 font-heading mb-2 group-hover:text-accent-700 transition-colors line-clamp-2">
                  {article.title}
                </h3>
                <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2">
                  {article.intro}
                </p>
                <div className="flex items-center gap-1.5 mt-4 text-xs font-semibold text-accent-600 group-hover:text-accent-700 transition-colors whitespace-nowrap">
                  Czytaj więcej
                  <i className="ri-arrow-right-line text-xs transition-transform duration-300 group-hover:translate-x-1"></i>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}